import {
  type AccessRuleInputData,
  useListAccessibleDeployments,
  useListAccessibleWorkspaces,
} from '@sunstone/api-client/hooks';
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@sunstone/ui';
import { Ban, ShieldCheck } from 'lucide-react';
import { EditableCombobox } from '@/components/ui/EditableCombobox';
import { useAuthToken } from '@/features/auth';
import { WORKSPACE_ENVIRONMENT_OPTIONS } from '@/features/workspaces/utils';
import { ATTRIBUTES_BY_RESOURCE_KIND } from '@/lib/permissions';
import { ALL_REGIONS } from '@/lib/regions';
import {
  type AccessRuleEffect,
  type RestrictionKind,
  type RuleDraft,
  ruleDraftConditionJson,
} from './accessRuleDraft';

interface EffectToggleProps {
  effect: AccessRuleEffect;
  onChange: (effect: AccessRuleEffect) => void;
}

function EffectToggle({ effect, onChange }: EffectToggleProps) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange('allow')}
        className={
          effect === 'allow'
            ? 'border-emerald-600 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950 dark:text-emerald-400'
            : ''
        }
      >
        <ShieldCheck className="h-4 w-4" />
        Allow
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange('deny')}
        className={
          effect === 'deny'
            ? 'border-destructive bg-destructive/10 text-destructive hover:bg-destructive/10'
            : ''
        }
      >
        <Ban className="h-4 w-4" />
        Deny
      </Button>
    </div>
  );
}

export interface RuleFieldsProps {
  tenantId: string;
  /** Scopes the preview to a partner's descendant tenants instead of
   * tenantId — forwarded from AccessRuleListEditor, same meaning as the
   * picker's own parentTenantId. */
  parentTenantId?: string;
  draft: RuleDraft;
  onChange: (draft: RuleDraft) => void;
  allowEffectToggle: boolean;
  picker: (draft: RuleDraft, onChange: (draft: RuleDraft) => void) => React.ReactNode;
  /** The same outer (role) and inner (user) envelope AccessRuleListEditor
   * already threads to this list's picker (accessrule.gateBoth) — folded
   * into the preview call too, so "Currently matches" reflects the sibling
   * kind's already-configured rule (e.g. a workspace-kind condition preview
   * narrows to only the deployments a deployment-kind rule already
   * restricts to), not raw attribute matches across everything. */
  roleRules?: AccessRuleInputData[];
  userRules?: AccessRuleInputData[];
}

/**
 * One resource-kind-scoped restriction: named (a picker) or a condition on
 * that kind's one attribute, plus a live "Currently matches" preview for the
 * condition mode. Shared shape between deployment-kind and workspace-kind
 * rows (SUN-408 round 3: rows are independent, not paired) — each caller
 * supplies its own picker and attribute list via `draft.resourceKind`, so
 * there's exactly one place each mode's UI is defined instead of two
 * near-duplicates.
 */
export function RuleFields({
  tenantId,
  parentTenantId,
  draft,
  onChange,
  allowEffectToggle,
  picker,
  roleRules,
  userRules,
}: RuleFieldsProps) {
  const { token } = useAuthToken();
  const attributes = ATTRIBUTES_BY_RESOURCE_KIND[draft.resourceKind];
  const conditionJson = ruleDraftConditionJson(draft);

  // effect is forced 'allow' here regardless of draft.effect — this preview
  // shows raw attribute matches, gate-independent by design (mirrors the
  // retired previewRuleBasedMatch): passing draft's real effect through
  // would flip a deny-effect draft's results to their complement instead of
  // showing what it actually matches. It's folded into userRules alongside
  // whatever this list's own userRules envelope already is — a different
  // resource_kind than any sibling rule already in there, so it can't
  // collide with (OR into) an existing row of the same kind.
  const previewRule: AccessRuleInputData | undefined =
    draft.restrictionKind === 'rule_based' && conditionJson
      ? {
          effect: 'allow',
          resourceKind: draft.resourceKind,
          restrictionKind: 'rule_based',
          ruleConditionJson: conditionJson,
        }
      : undefined;
  const previewUserRules = previewRule ? [...(userRules ?? []), previewRule] : (userRules ?? []);
  const depPreview = useListAccessibleDeployments(
    token,
    tenantId,
    parentTenantId,
    { pageSize: 100, roleRules, userRules: previewUserRules },
    !!previewRule && draft.resourceKind === 'deployment',
  );
  const wsPreview = useListAccessibleWorkspaces(
    token,
    tenantId,
    parentTenantId,
    { pageSize: 100, roleRules, userRules: previewUserRules },
    !!previewRule && draft.resourceKind === 'workspace',
  );
  const preview = draft.resourceKind === 'deployment' ? depPreview : wsPreview;
  const previewMatches = depPreview.data?.deployments ?? wsPreview.data?.workspaces ?? [];
  const previewTotal = depPreview.data?.totalCount ?? wsPreview.data?.totalCount ?? 0;

  function switchMode(restrictionKind: RestrictionKind) {
    onChange({ ...draft, restrictionKind, resourceIds: [], ruleAttribute: '', ruleValue: '' });
  }

  function switchEffect(effect: AccessRuleEffect) {
    onChange({ ...draft, effect, resourceIds: [], ruleAttribute: '', ruleValue: '' });
  }

  return (
    <div className="space-y-3">
      {allowEffectToggle && <EffectToggle effect={draft.effect} onChange={switchEffect} />}

      <Tabs value={draft.restrictionKind} onValueChange={(v) => switchMode(v as RestrictionKind)}>
        <TabsList>
          <TabsTrigger value="id_based">
            Specific {draft.resourceKind === 'deployment' ? 'deployments' : 'workspaces'}
          </TabsTrigger>
          <TabsTrigger value="rule_based">Match a condition</TabsTrigger>
        </TabsList>
      </Tabs>

      {draft.restrictionKind === 'id_based' ? (
        picker(draft, onChange)
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Applies to every current and future {draft.resourceKind} whose {attributes[0]} matches —
            not a fixed list.
          </p>
          <div className="flex items-center gap-2">
            <Select
              value={draft.ruleAttribute}
              onValueChange={(v) => onChange({ ...draft, ruleAttribute: v, ruleValue: '' })}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Attribute…" />
              </SelectTrigger>
              <SelectContent>
                {attributes.map((attr) => (
                  <SelectItem key={attr} value={attr}>
                    {attr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {draft.ruleAttribute && <span className="text-sm text-muted-foreground">equals</span>}
            {draft.ruleAttribute === 'environment' ? (
              <Select
                value={draft.ruleValue}
                onValueChange={(v) => onChange({ ...draft, ruleValue: v })}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Select environment…" />
                </SelectTrigger>
                <SelectContent>
                  {WORKSPACE_ENVIRONMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : draft.ruleAttribute === 'region' ? (
              <EditableCombobox
                value={draft.ruleValue}
                onChange={(v) => onChange({ ...draft, ruleValue: v })}
                options={ALL_REGIONS}
                placeholder="e.g. us-east-1"
                className="w-40"
              />
            ) : null}
          </div>
          <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Currently matches</p>
            {preview.isLoading ? (
              <p className="text-sm text-muted-foreground">Checking…</p>
            ) : previewMatches.length > 0 ? (
              <>
                <p className="text-sm font-medium">
                  {previewTotal} {draft.resourceKind}
                  {previewTotal === 1 ? '' : 's'} match{previewTotal === 1 ? 'es' : ''}.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {previewMatches.map((r) => (
                    <Badge key={r.id} variant="secondary">
                      {r.displayName}
                    </Badge>
                  ))}
                </div>
                {previewTotal > previewMatches.length && (
                  <p className="text-xs text-muted-foreground">
                    Showing first {previewMatches.length}.
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Nothing yet.</p>
            )}
            <p className="text-xs text-muted-foreground">
              Will also apply automatically to any future resource matching this condition.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
